import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { ContributionTypeService } from "services/contribution-type.service";
import { FundService } from "services/fund.service";
import {
  ISaveContributionType,
  IUpdateContributionType,
  IFundResponse,
} from "models/church.types";

export default function ContributionTypeRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [funds, setFunds] = useState<IFundResponse[]>([]);
  const [errors, setErrors] = useState<{ name?: string }>({});

  const [contributionType, setContributionType] = useState<
    ISaveContributionType | IUpdateContributionType
  >({
    name: "",
    fundId: null,
  });

  useEffect(() => {
    const initData = async () => {
      setLoadingFunds(true);
      try {
        // 1. Chargement des caisses
        const fundsResult = await FundService.getAll(0, 100);
        setFunds(fundsResult.data || []);

        // 2. Si mode édition, chargement des détails du type de contribution
        if (isEditMode && id) {
          const data = await ContributionTypeService.getById(parseInt(id, 10));
          setContributionType({
            id: data.id,
            name: data.name,
            fundId: data.fundId ?? null,
          });
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation des données :", error);
        toast.error("Impossible de charger les données requises");
        if (isEditMode) {
          history.push("/admin/contribution-types");
        }
      } finally {
        setLoadingFunds(false);
      }
    };

    initData();
  }, [id, isEditMode, history]);

  const validate = (): boolean => {
    const newErrors: { name?: string } = {};
    if (!contributionType.name.trim()) {
      newErrors.name = "Le nom du type est obligatoire";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setContributionType((prev) => ({
      ...prev,
      [name]: name === "fundId" ? (value ? Number(value) : null) : value,
    }));

    if (errors.hasOwnProperty(name)) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (isEditMode) {
        await ContributionTypeService.update(
          contributionType as IUpdateContributionType
        );
        toast.success("Type de contribution mis à jour avec succès");
      } else {
        await ContributionTypeService.create(
          contributionType as ISaveContributionType
        );
        toast.success("Type de contribution créé avec succès");
      }
      history.push("/admin/contribution-types");
    } catch (error) {
      console.error("Erreur d'enregistrement :", error);
      toast.error(
        "Erreur lors de l'enregistrement. Vérifiez si le nom existe déjà."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl">
          {/* Header */}
          <div className="rounded-t-xl bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-coins mr-2 text-[#D4AF37]"></i>
                {isEditMode
                  ? "Modifier le type"
                  : "Nouveau Type de Contribution"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Nom du Type */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-tag mr-2 text-[#D4AF37]"></i>Libellé du type
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={contributionType.name}
                    onChange={handleChange}
                    placeholder="Ex: Dîme, Offrande Ordinaire, Social Mariage ..."
                    className={`w-full bg-[#0F172A] border ${
                      errors.name ? "border-red-500" : "border-slate-700"
                    } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-[10px] mt-1 italic">
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Caisse Associée */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-wallet mr-2 text-[#D4AF37]"></i>Caisse destination
                  </label>
                  <div className="relative">
                    <select
                      name="fundId"
                      value={contributionType.fundId ?? ""}
                      onChange={handleChange}
                      disabled={loadingFunds}
                      className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all appearance-none cursor-pointer disabled:opacity-50"
                    >
                      <option value="">
                        -- Aucune caisse (Non rattachée) --
                      </option>
                      {funds.map((fund) => (
                        <option key={fund.id} value={fund.id}>
                          {fund.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                      {loadingFunds ? (
                        <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
                      ) : (
                        <i className="fas fa-chevron-down text-xs"></i>
                      )}
                    </div>
                  </div>
                  <p className="text-slate-500 text-[11px] mt-1 italic ml-1">
                    Les entrées financières enregistrées avec ce type seront affectées à cette caisse.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-10 flex justify-end gap-4 border-t border-slate-700/50 pt-6">
                <button
                  type="button"
                  onClick={() => history.push("/admin/contribution-types")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}